/**
 * Firebase Auth Sync Service
 *
 * This service ensures Firebase authentication state stays in sync with the Rails session.
 *
 * Key responsibilities:
 * 1. Listen for Firebase auth state changes (including restoration from localStorage)
 * 2. Automatically sync with Rails when Firebase auth is restored on page load
 * 3. Handle token refresh to prevent expiration during long sessions
 * 4. Update window.user for legacy code compatibility
 *
 * This solves the issue where:
 * - User logs in via Firebase (auth stored in localStorage)
 * - User navigates to plan_trail or refreshes the page
 * - Firebase restores auth from localStorage, but Rails session may have expired
 * - This service detects the Firebase auth and re-syncs with Rails
 */

import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { app } from './initialise.js';
import axiosClient from '../helpers/axios_client.js';
import { signalAuthReady } from './authReady';

// Track if we've already synced on this page load
let hasSyncedOnPageLoad = false;
let authListenerUnsubscribe: (() => void) | null = null;

/**
 * Fetch the backend user object and set window.user
 */
async function fetchAndSetBackendUser(): Promise<boolean> {
  try {
    const response = await axiosClient.get('/user/current');
    if (response.data) {
      (window as any).user = response.data;
      console.log('[AuthSync] Backend user fetched and set to window.user');
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[AuthSync] Failed to fetch backend user:', error);
    return false;
  }
}

/**
 * Sync Firebase auth with Rails session
 * Sends the Firebase ID token to the backend to establish/refresh the Rails session
 */
async function syncWithRails(user: User): Promise<boolean> {
  try {
    // Get a fresh token (force refresh to ensure it's valid)
    const token = await user.getIdToken(true);

    // Send to Rails to establish/refresh session
    const response = await fetch(`/firebase/auth.json?accessToken=${token}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      console.log('[AuthSync] Successfully synced Firebase auth with Rails session');
      // Fetch and set the backend user object
      await fetchAndSetBackendUser();
      return true;
    } else {
      console.warn('[AuthSync] Failed to sync with Rails:', response.status);
      return false;
    }
  } catch (error) {
    console.error('[AuthSync] Error syncing with Rails:', error);
    return false;
  }
}

/**
 * Handle Firebase auth state change
 */
async function handleAuthStateChange(user: User | null): Promise<void> {
  if (user) {
    // If we haven't synced on this page load and Rails might not know about the user,
    // sync with Rails to ensure the session is valid
    if (!hasSyncedOnPageLoad) {
      hasSyncedOnPageLoad = true;

      // Check if Rails already has a session by looking for user data injected by Rails
      const railsAlreadyHasSession = !!(window as any).currentUserId || !!(window as any).userSignedIn;

      if (!railsAlreadyHasSession) {
        console.log('[AuthSync] Firebase user found but Rails session may be missing, syncing...');
        // syncWithRails will also fetch and set the backend user
        await syncWithRails(user);
      } else {
        console.log('[AuthSync] Firebase user found and Rails session exists');
        // Rails session exists, fetch the backend user if not already set
        if (!(window as any).user) {
          await fetchAndSetBackendUser();
        }
      }
    }
  } else {
    // User signed out
    (window as any).user = null;
    hasSyncedOnPageLoad = false;
  }
}

/**
 * Initialize the auth sync service
 * Call this early in your application bootstrap
 */
export function initAuthSync(): void {
  if (authListenerUnsubscribe) {
    console.log('[AuthSync] Already initialized');
    return;
  }

  const auth = getAuth(app);

  console.log('[AuthSync] Initializing Firebase auth sync service');

  // Set up the auth state listener
  // signalAuthReady AFTER handleAuthStateChange completes so that
  // window.user is set before anything awaiting authReady proceeds.
  authListenerUnsubscribe = onAuthStateChanged(auth, async (user) => {
    try {
      await handleAuthStateChange(user);
    } finally {
      signalAuthReady();
    }
  });
}

/**
 * Stop the auth sync service
 */
export function stopAuthSync(): void {
  if (authListenerUnsubscribe) {
    authListenerUnsubscribe();
    authListenerUnsubscribe = null;
    console.log('[AuthSync] Stopped Firebase auth sync service');
  }
}

/**
 * Force a sync with Rails (useful after long periods of inactivity)
 */
export async function forceSync(): Promise<boolean> {
  const auth = getAuth(app);
  const user = auth.currentUser;

  if (user) {
    return syncWithRails(user);
  }

  console.log('[AuthSync] No Firebase user to sync');
  return false;
}

/**
 * Check if Firebase has a current user
 */
export function hasFirebaseUser(): boolean {
  const auth = getAuth(app);
  return !!auth.currentUser;
}

/**
 * Get the current Firebase user
 */
export function getCurrentFirebaseUser(): User | null {
  const auth = getAuth(app);
  return auth.currentUser;
}
